import {
    Component,
    Element,
    h,
    Host,
    Listen,
    Prop,
    State,
    Watch,
} from "@stencil/core";
import { selection } from "d3-selection";
import { arc, Arc, pie, PieArcDatum } from "d3-shape";
import "d3-transition"; // required, else webpack will wont include the transition function
import { debounce } from "../../../utils/debounce";
import { roundTo100Percent } from "../../../utils/percentage-calculation";
import { getUniqueId } from "../../../utils/unique-id";
import {
    parseIconFromName,
    RegistryInstance,
} from "../../iconset/iconset-registry";
import { ChartGroupDataValues } from "../interfaces/chart-data";
import {
    ChartDataGroupConfig,
    DonutChartDataGroupConfigItem,
} from "../interfaces/chart-data-group-config";
import { ChartMargin } from "../interfaces/chart-margin";
import { ChartState } from "../interfaces/chart-state";
import { DonutChartLabel } from "./donut-chart-label";
import { DonutChartSlice } from "./donut-chart-slice";
import { DonutChartWrapper } from "./donut-chart-wrapper";
import {
    labelInterpolator,
    polylineInterpolator,
} from "./donut-interpolations";
import { arcInterpolator as sliceInterpolator } from "../utils/interpolations";

export interface ChartDimensions {
    width: number;
    height: number;
    breakpoint: string;
    radius: number;
    labelFontSize: number;
    labelRadiusFactor: number;
    labelRadius: number;
    arcFn: Arc<any, PieArcDatum<DataEntry>>;
    outerArcFn: Arc<any, PieArcDatum<DataEntry>>;
}

interface LabelSpot {
    y: number;
    taken: boolean;
}

export interface DataEntry {
    key: string;
    value: number;
    percentage: number;
    percentageRounded: number;
    cssClass: string;
}

interface LabelDataEntry extends DataEntry {
    label: string;
    icon: string;
    textAnchor: string;
}

interface PieSlice<T> extends PieArcDatum<T> {
    sliceInterpolator: (
        arcFn: Arc<any, PieArcDatum<DataEntry>>,
        t: number
    ) => string;
}

interface LabelPositionInput<T> extends PieSlice<T> {
    targetY: number;
    midangle: number;
}

export interface PositionedPieLabel<T> extends LabelPositionInput<T> {
    x: number;
    y: number;
}

interface PieLabel<T> extends PositionedPieLabel<T> {
    polylineInterpolator: (size, t) => string;
    labelInterpolator: (t, yOffset) => string;
}

@Component({
    tag: "uic-donut-chart",
    styleUrl: "donut-chart.scss",
    shadow: false,
})
export class DonutChart {
    private pieFn = pie<DataEntry>()
        .sort(null) // Do not sort by size, use input sort order
        .value(function (d) {
            return d.value;
        });

    /** The data prop for binding. Each key in the object is one arc with it's proportions calculated from the value, given as number */
    @Prop()
    data: ChartGroupDataValues;

    /** The groupConfig property for binding the chart, it contains a CSS class and label field.
     * The CSS class is used for coloring the chart.
     */
    @Prop() groupConfig: ChartDataGroupConfig<
        ChartGroupDataValues,
        DonutChartDataGroupConfigItem
    >;

    /** Percentage formatter, set a custom format function for percentages to localize them correctly. */
    @Prop() percentageFormatter: (x: number) => string = (p) =>
        parseFloat((p * 100).toString()).toFixed(1) + "%";

    /** Value formatter, set a custom format function for chart value. */
    @Prop() valueFormatter: (x: number) => string = (v) => v.toString();

    /** Customize the margin. You don't have to change them, labels won't be truncated regardless of these values */
    @Prop() margin: ChartMargin = { top: 0, right: 0, bottom: 0, left: 0 };

    /** Can be used to define a custom bar chart root class for styling purposes.*/
    @Prop() rootClass: string = "";

    /** State in which the bar chart is currently in. */
    @Prop() state: ChartState = ChartState.SUCCESS;

    /** Text for displaying when no data is available. */
    @Prop() noDataText: string = "No data available";

    /** Set the transition duration. As long as the transitions do not cause any issues, keep the default */
    @Prop() transitionDurationMs: number = 500;

    /** id attribute for this chart. A unique id will be generated if this is empty */
    @Prop({ mutable: true }) chartId?: string = "";

    @State() transitionProgress = 0;

    @State() slices: PieSlice<DataEntry>[] = [];
    @State() labels: PieLabel<LabelDataEntry>[] = [];

    @State() dimensions: ChartDimensions = null;

    @Element() el: HTMLUicDonutChartElement;

    private readonly debouncedOnResizeHandler = debounce(() => {
        this.updateDimensions(this.el.parentElement.getBoundingClientRect());
        this.labels = this.calcLabels(
            this.dimensions,
            this.groupConfig,
            this.slices,
            this.labels
        );
    }, 100);

    constructor() {}

    connectedCallback() {
        this.updateDimensions(this.el.parentElement.getBoundingClientRect());
        this.updateData(this.data, []);
    }

    @Watch("chartId")
    componentWillLoad() {
        const { chartId } = this;
        this.chartId = chartId || getUniqueId();
    }

    @Watch("data")
    @Watch("groupConfig")
    triggerTransition() {
        this.updateData(this.data, this.slices);
    }

    @Listen("resize", { target: "window" })
    onResize() {
        this.debouncedOnResizeHandler();
    }

    private updateData(newData, currentPieData) {
        this.transitionProgress = 0;

        this.slices = this.buildSlices(
            newData,
            this.groupConfig,
            currentPieData
        );
        this.labels = this.calcLabels(
            this.dimensions,
            this.groupConfig,
            this.slices,
            this.labels
        );

        selection()
            .transition("pie-transition")
            .duration(this.transitionDurationMs)
            .tween("slice", () => {
                return (t) => (this.transitionProgress = t);
            });
    }

    private updateDimensions(parentBoundingBox) {
        const width = Math.max(parentBoundingBox.width, 10);
        const height = Math.min(width, parentBoundingBox.height);
        const isSmall = width < 250 ? true : false;
        const isMobile = width < 650 ? true : false;
        const labelFontSize = isSmall ? width * 0.1 : isMobile ? 50 : 42;
        const labelRadiusFactor = isMobile ? 1 : 1.1;
        const marginWidth =
            (isMobile ? 50 : width * 0.3) + // fixed on mobile because of icons
            this.margin.left +
            this.margin.right;
        const marginHeight = 60 + this.margin.top + this.margin.bottom;
        const radius =
            Math.min(width - 2 * marginWidth, height - 2 * marginHeight) / 2;
        // Specify the donut arc size
        const arcFn = arc<PieArcDatum<DataEntry>>()
            .innerRadius(radius * 0.5)
            .outerRadius(radius * 0.8);
        // Another arc that won't be drawn. Just for labels positioning
        const outerArcFn = arc<PieArcDatum<DataEntry>>()
            .innerRadius(radius * 0.8)
            .outerRadius(radius * 1);

        this.dimensions = {
            width,
            height,
            breakpoint: isSmall ? "small" : isMobile ? "medium" : "large",
            radius,
            labelFontSize,
            labelRadiusFactor,
            labelRadius: radius * labelRadiusFactor,
            arcFn,
            outerArcFn,
        };
    }

    private buildSlices(
        data: ChartGroupDataValues,
        groupConfig: ChartDataGroupConfig<
            ChartGroupDataValues,
            DonutChartDataGroupConfigItem
        >,
        prev: PieArcDatum<DataEntry>[]
    ): PieSlice<DataEntry>[] {
        if (!data) {
            return [];
        }

        // Convert data
        const dataEntries = Object.entries<number>(data)
            .filter(([, value]) => value > 0)
            .map(([k, v]) => ({ key: k, value: v }));

        const dataEntriesWithPercentage = roundTo100Percent(
            dataEntries.map((kv) => kv.value)
        )
            .map((p, i) => ({
                ...p,
                ...dataEntries[i],
                cssClass: this.getConfig(
                    groupConfig,
                    dataEntries[i].key,
                    (g) => g.cssClass,
                    ""
                ),
            }))
            .filter(
                ({ value, percentageRounded }) =>
                    value > 0 && percentageRounded > 0.0
            );

        return this.pieFn(dataEntriesWithPercentage).map((p, i) => ({
            ...p,
            sliceInterpolator: sliceInterpolator(p, prev[i]),
        }));
    }

    private calcLabels(
        { height: availableHeightPx, labelFontSize, outerArcFn, labelRadius },
        groupConfig: ChartDataGroupConfig<
            ChartGroupDataValues,
            DonutChartDataGroupConfigItem
        >,
        pieData: PieSlice<DataEntry>[],
        prev: PositionedPieLabel<DataEntry>[]
    ): PieLabel<LabelDataEntry>[] {
        const maxLabelCountPerSide = Math.floor(
            availableHeightPx / labelFontSize
        );
        const possibleLabelSpots = [...Array(maxLabelCountPerSide).keys()].map(
            (i) => ({
                y: labelFontSize * (i + 1) - availableHeightPx / 2,
                taken: false,
            })
        );

        const pieLabelData = pieData.map((d) => ({
            targetY: outerArcFn.centroid(d)[1],
            midangle: d.startAngle + (d.endAngle - d.startAngle) / 2,
            ...d,
        }));

        // seperate left and right to avoid label overlap
        const labelsRight = pieLabelData.filter((lp) => lp.midangle <= Math.PI);
        const labelsLeft = pieLabelData.filter((lp) => lp.midangle > Math.PI);

        const positionedLabelsRight = this.matchClosestSpot(
            labelsRight,
            possibleLabelSpots,
            labelRadius
        );
        const positionedLabelsLeft = this.matchClosestSpot(
            labelsLeft,
            possibleLabelSpots,
            labelRadius
        );

        return positionedLabelsRight
            .concat(positionedLabelsLeft)
            .map((p, i) => {
                return {
                    ...p,
                    data: this.addGroupProps(p, groupConfig),
                    polylineInterpolator: polylineInterpolator(
                        p,
                        prev[i],
                        this.dimensions
                    ),
                    labelInterpolator: labelInterpolator(p, prev[i]),
                };
            });
    }

    private matchClosestSpot(
        labels: LabelPositionInput<DataEntry>[],
        possibleLabelSpots: LabelSpot[],
        labelRadius: number
    ): PositionedPieLabel<DataEntry>[] {
        // copy possibleLabelSpots locally for each matching, so there will be no global  mutation
        // and one y position is usable on both sides of the chart
        const labelSpots = possibleLabelSpots.map((x) => Object.assign({}, x));

        return labels
            .map((l) => {
                const freeLabelSpots = labelSpots.filter((s) => !s.taken);

                if (freeLabelSpots.length <= 0) {
                    return undefined;
                }

                const closestSpot = freeLabelSpots.reduce((prev, curr) =>
                    Math.abs(curr.y - l.targetY) < Math.abs(prev.y - l.targetY)
                        ? curr
                        : prev
                );

                // update map of spots with taken flag
                const spotRef = labelSpots.find((ps) => ps.y === closestSpot.y);
                if (spotRef !== undefined) {
                    spotRef.taken = true;
                }

                return {
                    y: closestSpot.y,
                    x: labelRadius * (l.midangle < Math.PI ? 1 : -1),
                    ...l,
                };
            })
            .filter<PositionedPieLabel<DataEntry>>(this.notUndefinedOrNull);
    }

    private addGroupProps(p, groupConfig) {
        return {
            ...p.data,
            label: this.getConfig(
                groupConfig,
                p.data.key,
                (g) => g.label,
                p.data.key
            ),
            icon: this.getIconSrc(
                this.getConfig(groupConfig, p.data.key, (g) => g.icon, "")
            ),
            cssClass: this.getConfig(
                groupConfig,
                p.data.key,
                (g) => g.cssClass,
                ""
            ),
            textAnchor: p.midangle < Math.PI ? "start" : "end",
        };
    }

    // Useful for use in array.prototype.filter method when filtering undefined / null elements
    // it allows the types to be propagated correctly, so that after the filter elements
    // cannot be undefined / null anymore
    private notUndefinedOrNull<T>(x: T | undefined | null): x is T {
        return x !== undefined && x !== null;
    }

    private getConfig<T>(
        groupConfig: ChartDataGroupConfig<
            ChartGroupDataValues,
            DonutChartDataGroupConfigItem
        >,
        key: string,
        valueSelector: (c: DonutChartDataGroupConfigItem) => T,
        fallback: T
    ) {
        return this.notUndefinedOrNull(groupConfig) &&
            this.notUndefinedOrNull(groupConfig[key]) &&
            this.notUndefinedOrNull(valueSelector(groupConfig[key]))
            ? valueSelector(groupConfig[key])
            : this.notUndefinedOrNull(fallback)
            ? fallback
            : "";
    }

    private getIconSrc(iconName) {
        const icon = parseIconFromName(iconName);
        return RegistryInstance.resolveIconSrc(icon);
    }

    render() {
        return (
            <Host>
                <DonutChartWrapper
                    chartId={this.chartId}
                    rootClass={this.rootClass}
                    state={this.state}
                    noDataAvailable={
                        this.data == undefined ||
                        this.data == null ||
                        Object.keys(this.data).length === 0
                    }
                    noDataText={this.noDataText}
                    size={this.dimensions}
                >
                    <g class="uic-donut-chart__slices">
                        {this.slices.map((d) => (
                            <DonutChartSlice
                                dataGroupCssClass={d.data.cssClass}
                                pathInterpolator={d.sliceInterpolator}
                                dataGroupKey={d.data.key}
                                t={this.transitionProgress}
                                arcFn={this.dimensions.arcFn}
                                tooltipText={this.valueFormatter(d.data.value)}
                            />
                        ))}
                    </g>
                    <g class="uic-donut-chart__labels">
                        {this.labels.map((d) => (
                            <DonutChartLabel
                                d={d}
                                t={this.transitionProgress}
                                breakpoint={this.dimensions.breakpoint}
                                percentageFormatter={this.percentageFormatter}
                            />
                        ))}
                    </g>
                </DonutChartWrapper>
            </Host>
        );
    }
}
